import { c as _c } from "react/compiler-runtime";
import { useCustomQuery } from './useCustomQuery';
import { analyticsApi } from '../api/endpoints';
import type { EngagementAnalytics } from '../api/analytics-types';

// ============================================================================
// Shared Types
// ============================================================================

/**
 * Object types that support engagement analytics
 */
export type EngagementObjectType = 'entities' | 'entity' | 'product' | 'collection';

/**
 * Base parameters for engagement analytics queries
 */
export interface BaseEngagementAnalyticsParams {
  objectType: EngagementObjectType;
  objectId: number;
  startDate?: string;
  endDate?: string;
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Shared query key factory for engagement analytics
 * Supports multiple object types (entity, product, collection, etc.)
 */
export const engagementQueryKeys = {
  engagement: (objectType: EngagementObjectType, objectId: number, startDate?: string, endDate?: string) => [`${objectType}-analytics`, objectId, 'engagement', startDate, endDate] as const
} as const;

// ============================================================================
// API Client Functions
// ============================================================================

/**
 * Fetch engagement analytics for any supported object type
 *
 * This is the core implementation that can be reused for entitys, products, collections, etc.
 *
 * @param params - Query parameters including objectType, objectId, and optional date range
 * @returns Promise resolving to engagement analytics data
 */
async function fetchEngagementAnalytics(params: BaseEngagementAnalyticsParams): Promise<EngagementAnalytics> {
  return analyticsApi.getObjectEngagement(params.objectType, params.objectId, {
    startDate: params.startDate,
    endDate: params.endDate
  });
}

// ============================================================================
// Base Hook
// ============================================================================

/**
 * Base hook for fetching engagement analytics
 *
 * This is the core implementation that can be wrapped for specific object types.
 * Use the specialized hooks (useEntityEngagementAnalytics, useProductEngagementAnalytics, etc.)
 * instead of calling this directly.
 *
 * @param params - Base analytics query parameters
 * @returns Query result with engagement data, loading state, and error state
 */
export function useEngagementAnalytics(params) {
  const $ = _c(11);
  let t0;
  if ($[0] !== params.endDate || $[1] !== params.objectId || $[2] !== params.objectType || $[3] !== params.startDate) {
    t0 = engagementQueryKeys.engagement(params.objectType, params.objectId, params.startDate, params.endDate);
    $[0] = params.endDate;
    $[1] = params.objectId;
    $[2] = params.objectType;
    $[3] = params.startDate;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== params) {
    t1 = () => fetchEngagementAnalytics(params);
    $[5] = params;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = !!params.objectId;
  let t3;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: 10000,
      enabled: t2
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useCustomQuery(t3);
}

// ============================================================================
// Entity-Specific Hook
// ============================================================================

/**
 * Parameters for entity engagement analytics
 */
export interface UseEntityEngagementAnalyticsParams {
  entityId: number;
  startDate?: string;
  endDate?: string;
}

/**
 * Hook to fetch engagement analytics for a entity
 *
 * Fetches reaction and comment data for both entity-level and product-level engagement.
 * Uses TanStack Query with 5-minute staleTime for automatic caching and refetching.
 *
 * **Validates: Requirements 2.1, 2.2, 9.5**
 *
 * @param params - Analytics query parameters (entityId, optional date range)
 * @returns Query result with engagement data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useEntityEngagementAnalytics({
 *   entityId: 123,
 *   startDate: '2024-01-01',
 *   endDate: '2024-01-31'
 * });
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorMessage error={error} />;
 *
 * return (
 *   <div>
 *     <h2>Entity Reactions: {data.totalEntityReactions}</h2>
 *     <h2>Product Reactions: {data.totalProductReactions}</h2>
 *   </div>
 * );
 * ```
 */
export function useEntityEngagementAnalytics(params) {
  const $ = _c(4);
  let t0;
  if ($[0] !== params.endDate || $[1] !== params.entityId || $[2] !== params.startDate) {
    t0 = {
      objectType: "entities",
      objectId: params.entityId,
      startDate: params.startDate,
      endDate: params.endDate
    };
    $[0] = params.endDate;
    $[1] = params.entityId;
    $[2] = params.startDate;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return useEngagementAnalytics(t0);
}

// ============================================================================
// Future Hook Examples (for reference)
// ============================================================================

/**
 * Example: Hook for product engagement analytics
 *
 * export interface UseProductEngagementAnalyticsParams {
 *   productId: number;
 *   startDate?: string;
 *   endDate?: string;
 * }
 *
 * export function useProductEngagementAnalytics(params: UseProductEngagementAnalyticsParams) {
 *   return useEngagementAnalytics({
 *     objectType: 'product',
 *     objectId: params.productId,
 *     startDate: params.startDate,
 *     endDate: params.endDate,
 *   });
 * }
 */

/**
 * Example: Hook for collection engagement analytics
 *
 * export interface UseCollectionEngagementAnalyticsParams {
 *   collectionId: number;
 *   startDate?: string;
 *   endDate?: string;
 * }
 *
 * export function useCollectionEngagementAnalytics(params: UseCollectionEngagementAnalyticsParams) {
 *   return useEngagementAnalytics({
 *     objectType: 'collection',
 *     objectId: params.collectionId,
 *     startDate: params.startDate,
 *     endDate: params.endDate,
 *   });
 * }
 */